import { LegendRenderer } from 'geostyler-legend'
import OlStyleParser from 'geostyler-openlayers-parser'

import type { Rule, Symbolizer, Style as GeoStylerStyle } from 'geostyler-style'
import type OlStyle from 'ol/style/Style'

const styleParser = new OlStyleParser()

type PoziStyleRule = Rule & {
  isPoziBubble?: boolean
  poziBubbleFill?: string
}

class GeostylerLegendRenderer extends LegendRenderer {
  // This is a promise that waits for the style image to be loaded
  loadStyleImage = async (styleItem: OlStyle) => {
    // console.debug('GeostylerLegendRenderer - Loading style image', styleItem)

    return new Promise((resolve, reject) => {
      const onImgLoadSuccess = () => {
        // console.debug('Image really loaded.')
        return resolve(true)
      }

      const onImgLoadError = () => {
        return resolve(false)
      }

      if (!styleItem?.getImage) {
        return onImgLoadSuccess()
      }

      const imgStyle = styleItem.getImage()

      if (!imgStyle) {
        return onImgLoadSuccess()
      }

      // Check if image is already loaded
      if (imgStyle.getImageState() === 2) {
        return onImgLoadSuccess()
      }

      // Get the image element
      const img = imgStyle.getImage(1)

      if (!img) {
        return onImgLoadSuccess()
      }

      // @ts-expect-error
      img?.addEventListener?.('error', onImgLoadError)
      // @ts-expect-error
      img?.addEventListener?.('load', onImgLoadSuccess)

      // Force loading of image
      imgStyle.load()
    })
  }

  async getRuleIcon (rule: PoziStyleRule) : Promise<string> {
    // console.debug('GeostylerLegendRenderer - getRuleIcon', rule, this)

    // Check if style is a bubble icon, if so tidy it up for legend display
    if (rule.isPoziBubble) {
      // Assume that the last symbolizer is the icon
      const iconSymbolizer = rule.symbolizers[rule.symbolizers.length - 1]
      if (iconSymbolizer?.kind === 'Icon') {
        delete iconSymbolizer.offset
      }

      const simpleSquare: Symbolizer = {
        kind: 'Mark',
        wellKnownName: 'square',
        color: rule.poziBubbleFill,
        radius: iconSymbolizer?.kind === 'Icon' ? iconSymbolizer?.size : 20
      }

      rule.symbolizers = [simpleSquare, iconSymbolizer]
    }

    // Create a dummy style in order to load the images
    const style: GeoStylerStyle = {
      name: '',
      rules: [{
        name: '',
        symbolizers: rule.symbolizers
      }]
    }

    let { output: olStyle, errors = [] } = await styleParser.writeStyle(style)
    if (errors.length > 0) {
      console.log('GeostylerLegendRenderer - getRuleIcon - errors', errors)

      return Promise.reject(errors[0])
    }

    // console.debug('GeostylerLegendRenderer - getRuleIcon - style', olStyle)

    if (!olStyle) {
      return Promise.reject(new Error('No style found!'))
    }

    if (typeof olStyle === 'function') {
      // console.debug('GeostylerLegendRenderer - getRuleIcon - olStyle is function', olStyle)

      // Get the first geometry from the symbolizers and use that in the style function
      const firstGeom = rule.symbolizers.find(symbolizer => this.getGeometryForSymbolizer(symbolizer))
      olStyle = olStyle(firstGeom, 1)
    }

    // Make sure olStyle is an array
    if (olStyle && !Array.isArray(olStyle) && typeof olStyle !== 'function') {
      olStyle = [olStyle]
    }

    // We should never have the following situation but it's to keep typescript happy
    if (!Array.isArray(olStyle)) {
      return super.getRuleIcon(rule)
    }

    // Wait for all images to load
    await Promise.allSettled(olStyle.map((styleItem) => this.loadStyleImage(styleItem)))

    // Call the parent method for the actual rendering
    return super.getRuleIcon(rule)
  }
}

export default GeostylerLegendRenderer
